import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { DateTime } from "luxon";
import Icon from "components/atoms/Icon";
import Text from "components/atoms/Text";
import { COLOR_SYSTEM, ColorSystem } from "components/design/design-system";

/**
 * `/download` — PhAI 약국 설치 파일 다운로드 페이지
 * (spec ymyd-spec/docs/specs/2026-08-26-phai약국-설치파일-다운로드페이지-자동업로드.md Phase 2)
 *
 * 윤약국 약사가 직링크만으로 설치파일을 내려받아 설치까지 갈 수 있는 공개 페이지다.
 * 릴리스 때 웹 재배포가 필요 없도록, 버전·크기·빌드일·다운로드 URL을 S3의 `latest.json`에서 읽는다
 * (업로드 스크립트가 exe 업로드 성공 후에만 이 파일을 갱신한다 — spec §설계 결정 5).
 *
 * ⚠️ App.tsx의 **최상위 공개 라우트**(catch-all `/*` 앞)에 둔다. AuthenticatedApp 안에 두면
 *    비로그인 진입이 `/login`으로 튕겨 약사가 페이지를 볼 수 없다.
 * ⚠️ 사이트 어디에도 이 경로로 가는 링크를 두지 않는다 — 직링크 전용이다(spec §설계 결정 4).
 *    검색 유입은 `public/robots.txt`의 `Disallow: /download`로 막는다(§설계 결정 13).
 */

/** 매니페스트 URL은 이 모듈의 단일 상수다 — 설치본 자체가 dev 전용이라 환경 분기가 없다(spec §설계 결정 8). */
const MANIFEST_URL =
  "https://s3.ap-northeast-2.amazonaws.com/storage.dev.ymyd.onjourney.co.kr/phai/desktop/latest.json";

const ERROR_MESSAGE =
  "잠시 후 다시 시도해 주세요. 문제가 계속되면 전달받은 채널로 문의해 주세요.";

const WINDOWS_ONLY_NOTICE =
  "이 프로그램은 Windows 전용입니다. Windows PC에서 이 페이지를 열어주세요";

const INSTALL_STEPS = [
  "다운로드 후 파일을 실행하세요.",
  "파란 'Windows의 PC 보호' 창이 뜨면 [추가 정보] → [실행]을 눌러주세요.",
  "설치 중 관리자 권한 요청(예/아니오 창)이 뜨면 [예]를 눌러주세요.",
];

interface DesktopManifest {
  version: string;
  file: string;
  url: string;
  sizeBytes: number;
  builtAt: string;
}

type Phase =
  | { kind: "loading" }
  | { kind: "error" }
  | { kind: "ready"; manifest: DesktopManifest };

const color = (name: ColorSystem, shade: number): string =>
  ((COLOR_SYSTEM.get(name) as unknown) as Record<number, string> | undefined)?.[shade] ?? "#000000";

/** 매니페스트가 화면을 그릴 만큼 갖춰졌는지 확인한다 — 형식이 어긋나면 오류 상태와 동일하게 처리한다. */
const isDesktopManifest = (value: unknown): value is DesktopManifest => {
  const m = value as Partial<DesktopManifest> | null;
  return (
    !!m &&
    typeof m === "object" &&
    typeof m.version === "string" &&
    typeof m.file === "string" &&
    typeof m.url === "string" &&
    typeof m.sizeBytes === "number"
  );
};

/**
 * 빌드일은 파일명 스탬프를 KST로 해석한 값이므로(spec §계약면), 보는 사람의 시간대와 무관하게
 * 항상 KST로 표기한다. 값이 깨져 있으면 원문을 그대로 보여 주고 렌더는 계속한다.
 */
const formatBuiltAt = (raw: string): string => {
  const parsed = DateTime.fromISO(raw, { setZone: true });
  return parsed.isValid ? parsed.setZone("Asia/Seoul").toFormat("yyyy-MM-dd HH:mm") : raw;
};

/** 크기는 MB 반올림 정수로 표기한다(spec §화면 설계 3). 값이 온전치 않으면 표기를 생략한다. */
const formatSize = (sizeBytes: number): string | null =>
  Number.isFinite(sizeBytes) && sizeBytes > 0
    ? `약 ${Math.round(sizeBytes / 1024 / 1024)} MB`
    : null;

const DesktopDownload: React.FC = () => {
  const [phase, setPhase] = useState<Phase>({ kind: "loading" });

  // 폰·태블릿·맥은 전부 여기에 걸린다. 안내만 병기하고 다운로드는 막지 않는다(spec §화면 설계 3).
  const isWindows = useMemo(() => navigator.userAgent.indexOf("Windows") !== -1, []);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const response = await fetch(MANIFEST_URL, { cache: "no-store" });
        if (!response.ok) throw new Error(`매니페스트 응답 오류: HTTP ${response.status}`);

        const data = await response.json();
        if (!isDesktopManifest(data)) throw new Error("매니페스트 형식이 올바르지 않습니다");

        if (!cancelled) setPhase({ kind: "ready", manifest: data });
      } catch (error) {
        // 약사의 "안 돼요" 신고를 원격으로 진단하기 위한 유일한 단서다(spec §화면 설계 3).
        // eslint-disable-next-line no-console
        console.error("설치 파일 정보를 불러오지 못했습니다:", error);
        if (!cancelled) setPhase({ kind: "error" });
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const renderBody = () => {
    if (phase.kind === "loading") {
      return <Description>설치 파일 정보를 불러오는 중…</Description>;
    }

    if (phase.kind === "error") {
      return <Description>{ERROR_MESSAGE}</Description>;
    }

    const { manifest } = phase;
    const size = formatSize(manifest.sizeBytes);

    return (
      <>
        <MetaList>
          <MetaRow>
            <MetaLabel>버전</MetaLabel>
            <MetaValue>{`v${manifest.version}`}</MetaValue>
          </MetaRow>
          <MetaRow>
            <MetaLabel>빌드일</MetaLabel>
            <MetaValue>{formatBuiltAt(manifest.builtAt)}</MetaValue>
          </MetaRow>
          {size && (
            <MetaRow>
              <MetaLabel>크기</MetaLabel>
              <MetaValue>{size}</MetaValue>
            </MetaRow>
          )}
          <MetaRow>
            <MetaLabel>파일</MetaLabel>
            {/* 파일명은 버전 자릿수에 따라 길어질 수 있어 한 줄로 잘라 말줄임한다. */}
            <MetaValue
              data-testid="download-file-name"
              title={manifest.file}
              style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
            >
              {manifest.file}
            </MetaValue>
          </MetaRow>
        </MetaList>

        {!isWindows && <PlatformNotice>{WINDOWS_ONLY_NOTICE}</PlatformNotice>}

        {/*
          atoms/Button은 항상 <button>을 렌더해 href를 받을 수 없다 — 이 repo의 다운로드 선례
          (PrescriptionFormTemplate)대로 스타일드 <a>를 쓴다(spec §설계 결정 12).
          `download` 속성은 크로스 오리진이라 무시되므로 붙이지 않는다. 저장 파일명은 URL
          마지막 조각(스탬프 파일명)으로 자동 결정된다.
        */}
        <DownloadLink href={manifest.url}>
          <Icon name="Download" size={20} color={color("Gray", 0)} />
          <Text element="span" textType="InputLarge" color={color("Gray", 0)}>
            설치 파일 다운로드
          </Text>
        </DownloadLink>

        <GuideBlock>
          <GuideTitle>설치 안내</GuideTitle>
          <GuideList>
            {INSTALL_STEPS.map((step) => (
              <GuideItem key={step}>{step}</GuideItem>
            ))}
          </GuideList>
        </GuideBlock>
      </>
    );
  };

  return (
    <Page>
      <Card>
        <Title>PhAI 약국 설치 파일</Title>
        {renderBody()}
      </Card>
    </Page>
  );
};

export default DesktopDownload;

/* ── styles ─────────────────────────────────────────────────────────────────── */

const Page = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100vw;
  min-height: 100vh;
  padding: 24px;
  box-sizing: border-box;
  background-color: ${color("Gray", 50)};
`;

const Card = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 480px;
  padding: 40px 32px;
  border-radius: 16px;
  background-color: ${color("Gray", 0)};
  box-shadow: 0 8px 24px rgba(15, 32, 52, 0.08);
`;

const Title = styled.h1`
  margin: 0 0 20px;
  font-size: 22px;
  font-weight: 700;
  color: ${color("Gray", 900)};
`;

const Description = styled.p`
  margin: 0;
  font-size: 15px;
  line-height: 1.6;
  color: ${color("Gray", 500)};
`;

const MetaList = styled.dl`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 0 0 24px;
  padding: 16px;
  border-radius: 12px;
  background-color: ${color("Gray", 10)};
`;

const MetaRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 12px;
  min-width: 0;
`;

const MetaLabel = styled.dt`
  flex: 0 0 52px;
  font-size: 13px;
  color: ${color("Gray", 400)};
`;

const MetaValue = styled.dd`
  flex: 1 1 auto;
  min-width: 0;
  margin: 0;
  font-size: 15px;
  font-weight: 600;
  color: ${color("Gray", 800)};
`;

const PlatformNotice = styled.p`
  margin: 0 0 12px;
  padding: 12px 16px;
  border: 1px solid ${color("Yellow", 500)};
  border-radius: 10px;
  background-color: ${color("Yellow", 10)};
  font-size: 14px;
  line-height: 1.5;
  color: ${color("Gray", 700)};
`;

const DownloadLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 48px;
  padding: 0 24px;
  border-radius: 10px;
  background-color: ${color("Blue", 400)};
  text-decoration: none;
  transition: background-color 0.15s ease;

  &:hover {
    background-color: ${color("Blue", 500)};
  }
`;

const GuideBlock = styled.section`
  margin-top: 28px;
  padding-top: 20px;
  border-top: 1px solid ${color("Gray", 50)};
`;

const GuideTitle = styled.h2`
  margin: 0 0 12px;
  font-size: 15px;
  font-weight: 700;
  color: ${color("Gray", 800)};
`;

const GuideList = styled.ol`
  margin: 0;
  padding-left: 20px;
`;

const GuideItem = styled.li`
  margin-bottom: 8px;
  font-size: 14px;
  line-height: 1.6;
  color: ${color("Gray", 600)};

  &:last-child {
    margin-bottom: 0;
  }
`;
